'use client';

import { useEffect, useState } from 'react';
import { CookieConsentBanner } from '@web-store-platforms/components/cookie-consent/banner';
import { useCookieConsent } from '@web-store-platforms/components/cookie-consent';
import { APPCHARGE_COOKIE_POLICY_URL } from '@web-store-platforms/constants';
import { useTranslation } from 'react-i18next';
import { cookieConsentStorageAdapter } from '@/lib/services/cookieConsentStorage';
import { cn } from '@/lib/utils/cn';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';

/**
 * Shows the cookie banner as soon as login hydrates (no multi-second delay).
 *
 * The login logo must stay the largest painted element on the screen, so keep
 * the consent description's painted area below the *smallest* login logo
 * (`loginLogoSize: small` → 120×120 ≈ 14.4k px²). Stores with `big` logos
 * (~192²) clear it easily; small-logo stores did not when the description was
 * capped only at 4.5rem (≈24k px² on mobile).
 *
 * Do NOT hide the banner with opacity-0 / aria-hidden / pointer-events-none
 * while controls are mounted — keyboard users could still activate consent
 * without seeing the UI (Bugbot high: hidden consent still keyboard actionable).
 */
export function DeferredCookieConsentBanner(): React.ReactElement | null {
    const { t } = useTranslation();
    const [mount, setMount] = useState(false);
    const { showBanner, showSettingsDialog } = useCookieConsent();

    useLockBodyScroll(mount && (showBanner || showSettingsDialog));

    useEffect(() => {
        if (cookieConsentStorageAdapter.hasUserConsented()) {
            return;
        }
        setMount(true);
    }, []);

    if (!mount) {
        return null;
    }

    return (
        <div
            className={cn(
                // Area must stay under small logo (120²). At ~360px text width,
                // 1.75rem (28px) → ~10k px²; 4.5rem was ~24k and overtook the logo.
                '[&_[data-testid=cookie-consent-banner]_p]:!text-[10px]',
                '[&_[data-testid=cookie-consent-banner]_p]:!leading-tight',
                '[&_[data-testid=cookie-consent-banner]_p]:!max-h-[1.75rem]',
                '[&_[data-testid=cookie-consent-banner]_p]:!overflow-y-auto',
            )}
            data-testid="deferred-cookie-consent"
        >
            <CookieConsentBanner
                t={t}
                policyUrl={APPCHARGE_COOKIE_POLICY_URL}
            />
        </div>
    );
}
